import { PackageModulesFormView } from '../view/PackageModulesFormView';
import { templatePackageModulesForm } from '../view/template';
import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../../framework/BaseWebComponentController';
import { PackageModulesFormWebComponent } from '../component/PackageModulesFormWebComponent';
import { PackageModulesFormRepository } from '../repository/PackageModulesFormRepository';
import { PackageModulesFormViewData } from '../types/PackageModulesFormViewData';
import { OnOperationModeSelect } from '../../packageGeneralForm/types/PackageGeneralEvent';
import { Feature } from '@nirvana/shared/constants';
import { toggleVisibility } from '../../../../utils/htmlElement.utils';
import { DropdownEvents } from '../../../../ui/gzDropdown/types';
import { MonthlyProductType } from '../../../../license/types/MonthlyProductType';
import { ModulesData } from '../../../types/package-data';
import _ from 'lodash';
import { DeployerGridEvents } from '../../deployerGrid/types';
import { PackageType } from '../../../packagesGrid/types';
import { PackageActionContext } from '../../types/PackageActionContext';
import { ModuleFieldComponents } from '../types/ModuleFieldComponentsMap';
import { ToggleEvents } from '../../../../ui/gzToggle/types';
import { OnRemoveCompetitorsSelect } from '../types/PackageModulesEvent';
import { KitTypes } from '../../../downloadKitButtonSelector/constants';

export class PackageModulesFormController extends BaseWebComponentController {
  public component: PackageModulesFormWebComponent;
  public view: PackageModulesFormView;
  public viewConstructor: typeof PackageModulesFormView;
  public dataRepository: PackageModulesFormRepository;
  public dataRepositoryConstructor: typeof PackageModulesFormRepository;
  private availableFeatures: Feature[] | undefined;
  private packageType: PackageType | undefined;

  constructor(component: PackageModulesFormWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: PackageModulesFormView,
      viewTemplate: templatePackageModulesForm,
      dataRepositoryConstructor: PackageModulesFormRepository,
    });

    this.availableFeatures = component.getAvailableFeatures();
  }

  public async getDataForView(): Promise<PackageModulesFormViewData> {
    const operationModes = this.dataRepository.getOperationModes();

    return {
      operationModes: operationModes,
      context: this.component.formContext,
      availableFeatures: this.availableFeatures,
      displayOnlyModules: this.component.displayOnlyModules,
    };
  }

  public computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$operationModeFormField,
        event: DropdownEvents.OPTION_SELECTED,
        callback: this.onOperationModeSelect,
      },
      {
        element: this.view.dom.$showAllModulesToggle,
        event: ToggleEvents.TOGGLE_CHANGE,
        callback: this.showAllModules,
      },
      {
        element: this.view.modulesFields.networkProtection.$uiCheckbox,
        event: 'change',
        callback: this.handleNetworkProtectionCheckbox,
      },
      {
        element: this.view.modulesFields.userControl.$uiCheckbox,
        event: 'change',
        callback: this.handleUserControlCheckbox,
      },
      {
        element: this.view.modulesFields.antiphishing.$uiCheckbox,
        event: 'change',
        callback: this.handleAntiphishingCheckbox,
      },
      {
        element: this.view.modulesFields.trafficScan.$uiCheckbox,
        event: 'change',
        callback: this.handleTrafficScanCheckbox,
      },
      {
        element: this.view.modulesFields.networkMonitor.$uiCheckbox,
        event: 'change',
        callback: this.handleNetworkMonitorCheckbox,
      },
      {
        element: this.view.dom.$updateServerCheckbox,
        event: 'change',
        callback: this.handleUpdateServerCheckbox,
      },
      {
        element: this.view.dom.$removeCompetitorsCheckbox,
        event: 'change',
        callback: this.handleRemoveCompetitorsCheckbox,
      },
      {
        element: this.view.dom.$updateServerCheckbox,
        event: 'change',
        callback: this.dispatchUpdateServerCheckboxChange,
      },
    ];
  }

  public disableRelayCheckboxModule(state: boolean): void {
    this.view.dom.$updateServerCheckbox.readonly = state;
  }

  public toggleOffRelayModule(state: boolean): void {
    this.view.dom.$updateServerCheckbox.value = !state;
  }

  private dispatchUpdateServerCheckboxChange(): void {
    this.view.dom.$wrapper.dispatchEvent(
      new CustomEvent(DeployerGridEvents.RELAY_CHECKBOX_EVENT, {
        bubbles: true,
        composed: true,
        detail: {
          selected: this.view.dom.$updateServerCheckbox.value,
        },
      })
    );
  }

  private handleNetworkProtectionCheckbox(ev: WebComponentEvent): void {
    const isNetworkProtectionChecked = ev.detail.checked;
    this.view.modulesFields.userControl.$uiCheckbox.Input.checked =
      isNetworkProtectionChecked && !this.view.modulesFields.userControl.$uiCheckbox.classList.contains('hidden');
    this.view.modulesFields.antiphishing.$uiCheckbox.Input.checked =
      isNetworkProtectionChecked && !this.view.modulesFields.antiphishing.$uiCheckbox.classList.contains('hidden');
    this.view.modulesFields.trafficScan.$uiCheckbox.Input.checked =
      isNetworkProtectionChecked && !this.view.modulesFields.trafficScan.$uiCheckbox.classList.contains('hidden');
    this.view.modulesFields.networkMonitor.$uiCheckbox.Input.checked =
      isNetworkProtectionChecked && !this.view.modulesFields.networkMonitor.$uiCheckbox.classList.contains('hidden');
  }

  private handleUserControlCheckbox(ev: WebComponentEvent): void {
    this.view.modulesFields.networkProtection.$uiCheckbox.Input.checked =
      ev.detail.checked ||
      this.view.modulesFields.networkMonitor.$uiCheckbox.Input.checked ||
      this.view.modulesFields.antiphishing.$uiCheckbox.Input.checked ||
      this.view.modulesFields.trafficScan.$uiCheckbox.Input.checked;
  }

  private handleAntiphishingCheckbox(ev: WebComponentEvent): void {
    this.view.modulesFields.networkProtection.$uiCheckbox.Input.checked =
      ev.detail.checked ||
      this.view.modulesFields.networkMonitor.$uiCheckbox.Input.checked ||
      this.view.modulesFields.userControl.$uiCheckbox.Input.checked ||
      this.view.modulesFields.trafficScan.$uiCheckbox.Input.checked;
  }

  private handleTrafficScanCheckbox(ev: WebComponentEvent): void {
    this.view.modulesFields.networkProtection.$uiCheckbox.Input.checked =
      ev.detail.checked ||
      this.view.modulesFields.networkMonitor.$uiCheckbox.Input.checked ||
      this.view.modulesFields.userControl.$uiCheckbox.Input.checked ||
      this.view.modulesFields.antiphishing.$uiCheckbox.Input.checked;
  }

  private showAllModules(event: WebComponentEvent): void {
    this.component.showAllModules = event.detail.checked;
    toggleVisibility(this.view.dom.$showAllModulesBoxInfo, event.detail.checked);
    this.showCompatibleModules();
  }

  private handleNetworkMonitorCheckbox(ev: WebComponentEvent): void {
    this.view.modulesFields.networkProtection.$uiCheckbox.Input.checked =
      ev.detail.checked ||
      this.view.modulesFields.userControl.$uiCheckbox.Input.checked ||
      this.view.modulesFields.antiphishing.$uiCheckbox.Input.checked ||
      this.view.modulesFields.trafficScan.$uiCheckbox.Input.checked;
  }

  private handleUpdateServerCheckbox(): void {
    this.setPatchManagementServerCheckbox();
  }

  public setPatchManagementServerCheckbox(): void {
    const updateServerChecked = this.view.dom.$updateServerCheckbox.Input.checked;
    const isEndpointSecuritySelected = this.component.operationMode === MonthlyProductType.EndpointSecurity;

    const hasPatchManagement =
      this.availableFeatures?.[Feature.PRODUCT_FEATURE_PATCH_MANAGEMENT_USABLE] || this.isFrat();

    let isVisible = false;

    if (hasPatchManagement && updateServerChecked && isEndpointSecuritySelected) {
      isVisible = true;
    }

    if (!isVisible) {
      this.view.dom.$patchManagementServerCheckbox.Input.checked = false;
    }

    toggleVisibility(this.view.dom.$patchManagementServerCheckbox, isVisible);
    if (!updateServerChecked) {
      this.view.dom.$patchManagementServerCheckbox.Input.checked = false;
    }
    if (this.isFrat()) {
      this.view.dom.$patchManagementServerCheckbox.readonly = true;
    }
  }

  public setMailServersCheckbox(productType: MonthlyProductType): void {
    if (productType === MonthlyProductType.BitdefenderEdr) {
      toggleVisibility(this.view.dom.$mailServersCheckbox, false);
      return;
    }

    const hasExchange = !!this.availableFeatures?.[Feature.PRODUCT_FEATURE_EXCHANGE_INSTALL] || this.isFrat();
    this.view.dom.$mailServersCheckbox.readonly = !hasExchange || this.isFrat();
    toggleVisibility(this.view.dom.$mailServersCheckbox, hasExchange);
  }

  private handleRemoveCompetitorsCheckbox(ev: WebComponentEvent): void {
    toggleVisibility(this.view.dom.$removeCompetitorsBoxInfo, !ev.detail.checked);
    this.component.dispatchEvent(new OnRemoveCompetitorsSelect(ev.detail.checked));
  }

  private onOperationModeSelect(event: WebComponentEvent): void {
    if (event.detail.newValue.value === event.detail.oldValue?.value) {
      return;
    }

    this.showCompatibleModules();
    this.handleModulesAndRolesBasedOnProductType(event.detail.newValue.value);
    this.handleOsCompatibilityVisibilityForProductType();
    this.component.dispatchEvent(new OnOperationModeSelect(event.detail.newValue.value));
  }

  public handleModulesAndRolesBasedOnProductType(productType: MonthlyProductType): void {
    const isEndpointSecuritySelected = productType === MonthlyProductType.EndpointSecurity;

    this.view.modulesFields.edrSensor.$uiCheckbox.Input.readonly = !isEndpointSecuritySelected;

    if (!this.view.initialProductType || this.view.initialProductType !== productType) {
      this.view.dom.$removeCompetitorsCheckbox.Input.checked = isEndpointSecuritySelected;
      this.view.initialProductType = undefined;
    }

    toggleVisibility(this.view.dom.$edrBoxInfo, !isEndpointSecuritySelected);
    toggleVisibility(
      this.view.dom.$removeCompetitorsBoxInfo,
      !isEndpointSecuritySelected || !this.view.dom.$removeCompetitorsCheckbox.Input.checked
    );
    toggleVisibility(this.view.dom.$removeCompetitorsCheckbox, isEndpointSecuritySelected);
    toggleVisibility(this.view.dom.$additionalSettingsSubtitle, isEndpointSecuritySelected);

    this.setNetworkProtectionCheckbox();
    this.setPatchManagementServerCheckbox();
    this.setMailServersCheckbox(productType);
  }

  private isFrat(): boolean {
    return (
      Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_FRAT_VISIBLE]) ||
      this.packageType === PackageType.Frat ||
      this.packageType === PackageType.RelayForFrat
    );
  }

  public setAvailableFeatures(availableFeatures: Feature[]): void {
    this.availableFeatures = availableFeatures;
    this.showCompatibleModules();
  }

  public setOperationMode(): void {
    const hasCompanionStandalone = this.availableFeatures?.[Feature.PRODUCT_FEATURE_COMPANION_STANDALONE];
    const hasCompanionUsable = this.availableFeatures?.[Feature.PRODUCT_FEATURE_COMPANION_USABLE];
    const hasEdrSensor = this.availableFeatures?.[Feature.PRODUCT_FEATURE_EDR_USABLE];

    // used only on staging HELIX-7736
    if (this.component.selectedKitType && this.component.selectedKitType !== KitTypes.KIT_ID_SECURITY_TOOLS_WIN) {
      this.view.dom.$operationModeFormField.disabled = true;
      return;
    }

    if (!this.dependencies.hasMonthlyLicense() && hasEdrSensor && !hasCompanionStandalone && !hasCompanionUsable) {
      this.view.dom.$operationModeFormField.disabled = false;
      return;
    }

    this.view.dom.$operationModeFormField.Input.setFormValue(this.getDefaultOperationMode());

    let operationModeVisibility = false;
    if (this.dependencies.isPartner()) {
      operationModeVisibility = true;
      this.view.dom.$operationModeFormField.disabled = true;
    }

    toggleVisibility(this.view.dom.$operationModeFormField, operationModeVisibility);
  }

  public getDefaultOperationMode(): MonthlyProductType {
    const hasCompanionStandalone = this.availableFeatures?.[Feature.PRODUCT_FEATURE_COMPANION_STANDALONE];
    const hasCompanionUsable = this.availableFeatures?.[Feature.PRODUCT_FEATURE_COMPANION_USABLE];
    let defaultValueProductType = MonthlyProductType.EndpointSecurity;

    if (hasCompanionStandalone || hasCompanionUsable) {
      defaultValueProductType = MonthlyProductType.BitdefenderEdr;
    }

    return defaultValueProductType;
  }

  public showCompatibleModules(): void {
    if (
      Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_FRAT_VISIBLE]) &&
      this.component.formContext === PackageActionContext.Create
    ) {
      this.setFratModulesState();
      return;
    }

    for (const moduleField of Object.values(this.view.modulesFields)) {
      const isModuleAvailable = this.showSingleCompatibleModule(moduleField);
      if (this.component.useDefaultValues && isModuleAvailable) {
        moduleField.$uiCheckbox.Input.checked = moduleField.checked;
      }
    }

    this.setAntimalwareCheckbox();
    this.setNetworkProtectionCheckbox();
  }

  public getFalseValueModules() {
    // we need false as a default value when editing a package after adding an add-on that give access to more features
    const modules = {};
    for (const moduleField of Object.values(this.view.modulesFields)) {
      const isModuleAvailable = this.canUseModule(moduleField) || this.component.showAllModules;
      if (isModuleAvailable) {
        modules[moduleField.key] = false;
      }
    }
    return modules;
  }

  /**
   * The EDR product type is available only on Windows
   * for all the modules that ar visible on EDr product type we should ensure we display only the Win icon
   * @private
   */
  private handleOsCompatibilityVisibilityForProductType(): void {
    const isVisibleForProductType = this.view.dom.$operationModeFormField.value === MonthlyProductType.EndpointSecurity;
    this.view.dom.$moduleLinuxTooltip.forEach(el => {
      toggleVisibility(el, isVisibleForProductType);
    });

    this.view.dom.$moduleMacOsTooltip.forEach(el => {
      toggleVisibility(el, isVisibleForProductType);
    });
  }

  private setFratModulesState(): void {
    if (this.isFrat()) {
      for (const moduleField of Object.values(this.view.modulesFields)) {
        moduleField.$uiCheckbox.Input.checked = false;
        moduleField.$uiCheckbox.readonly = true;
      }
      this.view.dom.$removeCompetitorsCheckbox.Input.checked = false;
      this.view.modulesFields.frat.$uiCheckbox.Input.checked = true;
      this.view.dom.$removeCompetitorsCheckbox.readonly = true;
    }
  }

  private setNetworkProtectionCheckbox(): void {
    this.view.modulesFields.networkProtection.$uiCheckbox.disabled =
      this.view.modulesFields.userControl.$uiCheckbox.disabled &&
      this.view.modulesFields.networkMonitor.$uiCheckbox.disabled &&
      this.view.modulesFields.antiphishing.$uiCheckbox.disabled &&
      this.view.modulesFields.trafficScan.$uiCheckbox.disabled;

    const isUserControlHidden = this.view.modulesFields.userControl.$uiCheckbox.classList.contains('hidden');
    const isNetworkMonitorHidden = this.view.modulesFields.networkMonitor.$uiCheckbox.classList.contains('hidden');

    this.view.modulesFields.networkProtection.$uiCheckbox.Input.checked =
      (!isUserControlHidden && this.view.modulesFields.userControl.$uiCheckbox.Input.checked) ||
      (!isNetworkMonitorHidden && this.view.modulesFields.networkMonitor.$uiCheckbox.Input.checked) ||
      this.view.modulesFields.antiphishing.$uiCheckbox.Input.checked ||
      this.view.modulesFields.trafficScan.$uiCheckbox.Input.checked;
  }

  private setAntimalwareCheckbox(): void {
    this.view.modulesFields.antimalware.$uiCheckbox.readonly = true;
    this.view.modulesFields.antimalware.$uiCheckbox.Input.checked = this.component.useDefaultValues;
  }

  private showSingleCompatibleModule(moduleField: ModuleFieldComponents): boolean {
    const isModuleAvailable = this.canUseModule(moduleField) || this.component.showAllModules;

    const isReadOnly = this.view.dom.$operationModeFormField.Input.value === MonthlyProductType.BitdefenderEdr;
    toggleVisibility(moduleField.$uiCheckbox, isModuleAvailable);
    moduleField.$uiCheckbox.readonly = isReadOnly;

    return isModuleAvailable;
  }

  private canUseModule(moduleField: ModuleFieldComponents): boolean {
    let canUse = false;
    if (!_.includes(moduleField.availableForProducts, this.view.dom.$operationModeFormField.Input.value)) {
      return false;
    }

    if (moduleField.availableForFeatures.length === 0) {
      return true;
    }

    moduleField.availableForFeatures.every(el => {
      if (!this.availableFeatures || this.availableFeatures[el]) {
        canUse = true;
        return false;
      }
      return true;
    });

    return canUse;
  }

  private handleRelayRoleAvailabilityForFrat(): void {
    if (this.packageType === PackageType.Frat || this.packageType === PackageType.RelayForFrat) {
      this.disableRelayCheckboxModule(true);
    }
  }

  public setPackageType(packageType: PackageType) {
    this.packageType = packageType;
  }

  public setPackageData(packageData: ModulesData): void {
    if (packageData.productType === undefined) {
      packageData.productType = MonthlyProductType.EndpointSecurity;
    }
    this.view.setPackageData(packageData);
    this.setFratModulesState();
    this.handleRelayRoleAvailabilityForFrat();
  }
}
